import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { GitMerge, RotateCcw, X } from 'lucide-react'
import MergeCheckbox from './MergeCheckbox'
import { collectionsApi, type DuplicateCluster } from '../../services/collectionsApi'

/**
 * The minimum a row needs to be rendered here. Structural rather than
 * `CollectionItem`, because the Places page's rows come from
 * `usePlacesMap` and never were collection items.
 */
interface DuplicateReviewItem {
    id: number
    data?: Record<string, any> | null
    created_at?: string | null
}

/**
 * Fields the row renders itself, plus the bookkeeping ones that say nothing
 * about who someone is. Everything else filled in becomes a highlight.
 */
const SELF_RENDERED = new Set([
    'first name', 'last name', 'name', 'nickname', 'relation', 'notes',
    'picture', 'schema_version',
])

interface DuplicateReviewProps {
    collectionId: number
    clusters: DuplicateCluster[]
    dismissedClusters: DuplicateCluster[]
    /** Every item on the page, for hydrating a cluster's ids into rows. */
    items: DuplicateReviewItem[]
    /** Hand a cluster to the existing merge review modal. */
    onMerge: (itemIds: number[]) => void
    /** A dismissal or a restore landed; re-read the counts. */
    onChanged: () => void | Promise<void>
    /** Drop into the old free-form multi-select, for a pair this never grouped. */
    onManualSelect: () => void
    /**
     * Open the page's own read-only detail dialog for one row. Each surface
     * owns a different one — `PersonDetailModal` here, `CollectionItemPeekModal`
     * there — so the review stays out of the business of knowing which.
     * Reached from the check-in count and nothing else. A separate "See more"
     * button sat here briefly and earned its removal: those dialogs ARE the
     * check-ins view for their collection — `CollectionItemPeekModal` says so
     * outright ("People and Places reuse PersonDetailModal /
     * LocationDetailModal") — so the two affordances opened the same thing,
     * and the second one stranded itself on its own line whenever a row had
     * no highlights to sit beside.
     *
     * Omitted, the count renders as plain text.
     */
    onOpenDetail?: (itemId: number) => void
    onDone: () => void
}

/**
 * The grouped list behind the "N name collisions" badge.
 *
 * A count is not actionable and a flat filter is barely better: duplicates are
 * a PARTITION, not a set, so ten rows in a grid say nothing about which two go
 * together and a three-way cluster is worse. Resolution happens per group.
 *
 * It does not reuse the card grid, because the cards omit the field that
 * actually decides these. Every call made while cleaning up one account turned
 * on `notes` or `nickname` — "ThinkOlio founder" against a bare Chris,
 * "Mexican Alex", "Jennifer Chan's sister" — and two identical-looking cards
 * would send you opening each one in turn. So each row shows what distinguishes
 * it: nickname, relation, notes, how many check-ins point at it, and when it
 * was made.
 *
 * Two outcomes, because a cluster has two honest answers. "Merge these" opens
 * the existing propose/review/commit flow. "Different people" records a verdict
 * keyed on the id set, which is the answer for the Chrises — they will never
 * merge, and without it the badge would sit at its count forever and stop
 * meaning anything.
 */
export default function DuplicateReview({
    collectionId,
    clusters,
    dismissedClusters,
    items,
    onMerge,
    onChanged,
    onManualSelect,
    onOpenDetail,
    onDone,
}: DuplicateReviewProps) {
    const [counts, setCounts] = useState<Record<number, number>>({})
    const [busy, setBusy] = useState<string | null>(null)
    const [showDismissed, setShowDismissed] = useState(false)
    // Per-cluster opt-outs, by cluster key. Absent means "nothing excluded" —
    // storing the exclusions rather than the selection keeps the default
    // (everything ticked) true for a cluster this has never seen, including
    // one that appears after a merge reshapes the group.
    const [excluded, setExcluded] = useState<Record<string, Set<number>>>({})

    // Tag counts only for the rows on screen. The whole-collection version of
    // this question is the expensive one the health endpoint exists to avoid;
    // a couple of dozen ids is not.
    useEffect(() => {
        const ids = [...clusters, ...dismissedClusters].flatMap((c) => c.item_ids)
        if (ids.length === 0) {
            setCounts({})
            return
        }
        let live = true
        collectionsApi.items
            .referenceCounts(collectionId, ids)
            .then((next) => { if (live) setCounts(next) })
            .catch((err) => console.error('Failed to load reference counts:', err))
        return () => { live = false }
    }, [collectionId, clusters, dismissedClusters])

    const byId = new Map(items.map((it) => [it.id, it]))

    const selectedIn = (cluster: DuplicateCluster) => {
        const out = excluded[cluster.cluster_key]
        return cluster.item_ids.filter((id) => !out?.has(id))
    }

    const toggle = (cluster: DuplicateCluster, itemId: number) => {
        setExcluded((prev) => {
            const next = new Set(prev[cluster.cluster_key] ?? [])
            if (next.has(itemId)) next.delete(itemId)
            else next.add(itemId)
            return { ...prev, [cluster.cluster_key]: next }
        })
    }

    const dismiss = async (cluster: DuplicateCluster, itemIds: number[]) => {
        setBusy(cluster.cluster_key)
        try {
            await collectionsApi.items.dismissDuplicates(collectionId, itemIds)
            await onChanged()
        } catch (err) {
            console.error('Failed to dismiss duplicate cluster:', err)
        } finally {
            setBusy(null)
        }
    }

    const restore = async (cluster: DuplicateCluster) => {
        setBusy(cluster.cluster_key)
        try {
            await collectionsApi.items.undismissDuplicates(collectionId, cluster.cluster_key)
            await onChanged()
        } catch (err) {
            console.error('Failed to restore duplicate cluster:', err)
        } finally {
            setBusy(null)
        }
    }

    const renderRow = (
        itemId: number,
        cluster: DuplicateCluster,
        selectable: boolean,
    ) => {
        const item = byId.get(itemId)
        const data: Record<string, any> = item?.data ?? {}
        const name = [data['first name'], data['last name']].filter(Boolean).join(' ')
            || data.name || '(no name)'
        const nickname = data.nickname
        const relation = data.relation
        const notes = data.notes
        // Highlights: every other filled field, so two rows that differ only
        // on a phone number or an Instagram handle can be told apart here
        // rather than by opening both. Name, nickname, relation and notes are
        // rendered on their own above, and the bookkeeping columns say nothing
        // about who this is.
        const highlights = Object.entries(data).filter(([key, value]) =>
            !SELF_RENDERED.has(key)
            && typeof value === 'string'
            && value.trim() !== ''
        ) as [string, string][]
        const count = counts[itemId]
        const created = item?.created_at
            ? new Date(item.created_at).toLocaleDateString(undefined,
                { month: 'short', day: 'numeric', year: 'numeric' })
            : null

        const checked = !excluded[cluster.cluster_key]?.has(itemId)

        return (
            <div
                key={itemId}
                className={`py-2 border-t border-gray-100 first:border-t-0 flex gap-3 ${
                    selectable && !checked ? 'opacity-45' : ''
                }`}
            >
                {selectable && (
                    <div className="pt-0.5">
                        <MergeCheckbox checked={checked} onToggle={() => toggle(cluster, itemId)} />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-medium text-gray-900">{name}</span>
                    {nickname && (
                        <span className="text-sm text-gray-500">“{nickname}”</span>
                    )}
                    {relation && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                            {relation}
                        </span>
                    )}
                    <span className="ml-auto text-xs text-gray-500 tabular-nums whitespace-nowrap">
                        {/* The count is the most load-bearing number here — which
                            row the check-ins actually hang off usually decides
                            the survivor — so it opens them rather than just
                            reporting how many there are. */}
                        {typeof count === 'number' ? (
                            onOpenDetail ? (
                                <button
                                    onClick={() => onOpenDetail(itemId)}
                                    className="text-amber-800 hover:text-amber-900 hover:underline"
                                >
                                    {count} check-in{count === 1 ? '' : 's'}
                                </button>
                            ) : (
                                `${count} check-in${count === 1 ? '' : 's'}`
                            )
                        ) : '…'}
                        {created && <span className="text-gray-400"> · {created}</span>}
                    </span>
                </div>
                {notes ? (
                    <p className="mt-0.5 text-sm text-gray-600">{notes}</p>
                ) : (
                    <p className="mt-0.5 text-sm text-gray-400 italic">no notes</p>
                )}
                {highlights.length > 0 && (
                    <div className="mt-1 flex items-center gap-x-3 gap-y-1 flex-wrap">
                        {highlights.map(([key, value]) => (
                            <span key={key} className="text-xs text-gray-500">
                                <span className="text-gray-400">{key}</span>{' '}
                                {value}
                            </span>
                        ))}
                    </div>
                )}
                </div>
            </div>
        )
    }

    const renderCluster = (cluster: DuplicateCluster, settled: boolean) => {
        const chosen = selectedIn(cluster)
        // Both buttons act on what is ticked — the one rule that needs no
        // explaining. Either verdict about a single row is meaningless, so
        // both need two.
        const enough = chosen.length >= 2
        const partial = chosen.length < cluster.item_ids.length

        return (
        <motion.div
            key={cluster.cluster_key}
            layout
            initial={{ opacity: 0, scale: 0.95, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -8 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            style={{ transformOrigin: 'top center' }}
            className={`rounded-lg border p-3 ${
                settled ? 'border-gray-200 bg-gray-50' : 'border-amber-200 bg-white'
            }`}
        >
            <div className="flex items-center justify-between gap-2 mb-1">
                <h3 className="text-sm font-semibold text-gray-800">
                    {cluster.label}
                    <span className="ml-2 font-normal text-gray-500">
                        {settled || !partial
                            ? `${cluster.item_ids.length} entries`
                            : `${chosen.length} of ${cluster.item_ids.length} selected`}
                    </span>
                </h3>
                {settled ? (
                    <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        onClick={() => restore(cluster)}
                        disabled={busy === cluster.cluster_key}
                        className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 disabled:opacity-50"
                    >
                        <RotateCcw className="w-3 h-3" />
                        Restore
                    </motion.button>
                ) : (
                    <div className="flex items-center gap-2">
                        <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                            onClick={() => onMerge(chosen)}
                            disabled={!enough}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-amber-100 text-amber-900 hover:bg-amber-200 disabled:opacity-40 disabled:hover:bg-amber-100"
                        >
                            <GitMerge className="w-3 h-3" />
                            Merge{partial ? ` these ${chosen.length}` : ' these'}
                        </motion.button>
                        <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                            onClick={() => dismiss(cluster, chosen)}
                            disabled={!enough || busy === cluster.cluster_key}
                            className="px-2.5 py-1 rounded-md text-xs text-gray-600 border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
                        >
                            Different people
                        </motion.button>
                    </div>
                )}
            </div>
            {cluster.item_ids.map((id) => renderRow(id, cluster, !settled))}
        </motion.div>
        )
    }

    return (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-800">
                    {clusters.length > 0
                        ? `Reviewing ${clusters.length} name collision${clusters.length === 1 ? '' : 's'}`
                        : 'No name collisions left'}
                </h2>
                <div className="flex items-center gap-3">
                    {/* Grouping only finds rows that render alike. Two spellings
                        of one name never cluster, so the free-form picker has to
                        stay one click away. */}
                    <button
                        onClick={onManualSelect}
                        className="text-xs text-gray-500 hover:text-gray-800"
                    >
                        Select manually
                    </button>
                    <motion.button
                        whileHover={{ scale: 1.1, rotate: 90 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        onClick={onDone}
                        aria-label="Close duplicate review"
                        className="text-gray-400 hover:text-gray-700"
                    >
                        <X className="w-4 h-4" />
                    </motion.button>
                </div>
            </div>

            <div className="space-y-3">
                <AnimatePresence initial={false}>
                    {clusters.map((c) => renderCluster(c, false))}
                </AnimatePresence>
            </div>

            {dismissedClusters.length > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-100">
                    <button
                        onClick={() => setShowDismissed((v) => !v)}
                        className="text-xs text-gray-500 hover:text-gray-800"
                    >
                        {showDismissed ? 'Hide' : 'Show'} {dismissedClusters.length} marked
                        “different people”
                    </button>
                    {showDismissed && (
                        <div className="space-y-3 mt-3">
                            <AnimatePresence initial={false}>
                                {dismissedClusters.map((c) => renderCluster(c, true))}
                            </AnimatePresence>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
